import { useState, useEffect } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/app";
import { CloudArrowDown, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

/**
 * TiDo synchronizácia — načíta realita + plan z TiDo API do lokálnej DB.
 * Zobrazí sa iba pre strediská s vyplneným `tido_center_id`.
 */
export default function TidoSyncCard({ storeId }) {
  const { data: store } = useSWR(`/stores/${storeId}`, fetcher);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [includeRealita, setIncludeRealita] = useState(true);
  const [includePlan, setIncludePlan] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (store?.data_range?.min && store?.data_range?.max) {
      // If store has existing data, use those bounds as sensible defaults
    }
  }, [store]);

  if (!store) return null;
  if (!store.tido_center_id) {
    return (
      <div className="card-flat p-4 border-dashed border-2 border-[var(--border-default)]">
        <div className="flex items-start gap-3">
          <CloudArrowDown size={22} className="text-[var(--text-muted)] shrink-0 mt-0.5" />
          <div>
            <h4 className="font-display text-sm font-semibold">TiDo synchronizácia</h4>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              Toto stredisko nemá nastavené <code className="bg-[var(--bg-page)] px-1 rounded">tido_center_id</code>.
              Otvorte &quot;Konfigurácia strediska&quot; a doplňte ho, aby ste mohli sťahovať dáta priamo z TiDo API.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const preset = (days) => {
    const t = new Date();
    const start = new Date(t);
    start.setDate(start.getDate() - (days - 1));
    setFrom(start.toISOString().slice(0, 10));
    setTo(t.toISOString().slice(0, 10));
  };

  const sync = async () => {
    if (!from || !to) {
      toast.error("Vyplňte obdobie od-do");
      return;
    }
    setSyncing(true);
    setResult(null);
    try {
      const { data } = await api.post(`/tido/sync/${storeId}`, {
        date_from: from,
        date_to: to,
        include_realita: includeRealita,
        include_plan: includePlan,
      });
      setResult({ ok: true, ...data });
      const rParts = [];
      if (data.realita) rParts.push(`realita: ${data.realita.imported} záznamov`);
      if (data.plan) rParts.push(`plán: ${data.plan.imported} záznamov`);
      toast.success(`TiDo sync hotový (${rParts.join(", ")})`);
      mutate(`/stores/${storeId}/data-range`);
      mutate(`/stores/${storeId}/plans/range`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Chyba pri TiDo sync";
      setResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="card-flat p-4 bg-[var(--brand)]/5 border-[var(--brand)]/30">
      <div className="flex items-start gap-3 mb-3">
        <CloudArrowDown size={22} className="text-[var(--brand)] shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1">
          <h4 className="font-display text-sm font-semibold">Synchronizovať z TiDo</h4>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Prepojené s TiDo center_id <strong>{store.tido_center_id}</strong>. Predvolene sťahujeme <strong>posledných 30 dní</strong> — po uzavretí účtovníctva by staršie dáta už nemali meniť.
            Väčšie rozsahy použite len ak ste na to explicitne odkázaní (výnimočný re-import, oprava historickej chyby).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className="label-eyebrow block mb-1">Od</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="tabular text-xs" data-testid="tido-sync-from" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Do</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="tabular text-xs" data-testid="tido-sync-to" />
        </div>
        <div className="flex gap-1">
          {[
            { id: "7d", label: "7d", days: 7 },
            { id: "30d", label: "30d", days: 30 },
            { id: "90d", label: "Kvartál", days: 90 },
            { id: "365d", label: "Rok", days: 365 },
          ].map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => preset(p.days)}
              className="text-[11px] px-2 py-1 rounded-full border border-[var(--border-default)] bg-white text-[var(--text-secondary)] hover:border-[var(--brand)]"
              data-testid={`tido-sync-preset-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-3 text-xs">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={includeRealita} onChange={(e) => setIncludeRealita(e.target.checked)} data-testid="tido-sync-realita" />
          Realita (hodinové tržby & obsadenie)
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={includePlan} onChange={(e) => setIncludePlan(e.target.checked)} data-testid="tido-sync-plan" />
          Plán personálu
        </label>
      </div>

      <button
        onClick={sync}
        disabled={syncing || (!includeRealita && !includePlan)}
        className="btn-primary text-sm"
        data-testid="tido-sync-btn"
      >
        {syncing ? "Synchronizujem…" : "Spustiť synchronizáciu"}
      </button>

      {result && result.ok && (
        <div className="mt-3 text-xs text-[var(--success)] flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
          <div className="tabular">
            {result.realita && (
              <div>Realita: <strong>{result.realita.imported}</strong> záznamov · {result.realita.dates} dní ({result.realita.first_date} → {result.realita.last_date})</div>
            )}
            {result.plan && (
              <div>Plán: <strong>{result.plan.imported}</strong> záznamov · {result.plan.dates} dní</div>
            )}
          </div>
        </div>
      )}
      {result && !result.ok && (
        <div className="mt-3 text-xs text-[var(--critical)] flex items-start gap-2">
          <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
          <div>{result.error}</div>
        </div>
      )}
    </div>
  );
}
